import React, { useEffect, useState, useMemo } from "react";
import FullPageSpinner from "../layout/FullPageSpinner";
import {
  fetchAllBundles,
  createBundle,
  updateBundle,
  deleteBundle,
  syncBundle,
  previewSyncBundle,
  fetchAcademicYears,
} from "../../actions/product";
import QuickViewModal from "../parenPortal/products/QuickViewModal";
import BundleFormDialog from "../layout/BundleFormDialog";
import ConfirmModal from "../layout/ConfirmModal";
import SyncPreviewModal from "./SyncPreviewModal";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const BundleManagement = () => {
  const { user } = useSelector(
    (state) => state.auth
  );

  const hasPermission = (permission) => {
    if (user?.role === 'superadmin') return true;
    return user?.permissions?.[permission] === true;
  };

  const canAddBundle = hasPermission('bundle_add');
  const canEditBundle = hasPermission('bundle_edit');
  const canSyncBundle = hasPermission('bundle_sync');
  const canDeleteBundle = hasPermission('bundle_delete');
  const canViewBundleDetails = hasPermission('bundle_details_view');

  const [bundles, setBundles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [showBundleForm, setShowBundleForm] = useState(false);
  const [editBundle, setEditBundle] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [syncPreviewData, setSyncPreviewData] = useState(null);
  const [showSyncPreview, setShowSyncPreview] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [productType, setProductType] = useState('');
  const [studentType, setStudentType] = useState('');
  const [currentYear, setCurrentYear] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      loadBundles(selectedYear, studentType, productType);
    }
  }, [selectedYear, studentType, productType]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const academicYearsRes = await fetchAcademicYears();

      if (academicYearsRes) {
        setAcademicYears(academicYearsRes);

        // find current year using is_current flag
        const current =
          academicYearsRes.find(
            (year: any) => year.is_current === true
          ) || academicYearsRes[0];
        setCurrentYear(current.year)
        setSelectedYear(current.year);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadBundles = async (year, studentType, productType) => {
    setLoading(true);
    try {
      const response = await fetchAllBundles(year, studentType, productType);
      setBundles(Array.isArray(response) ? response : []);
    } catch (error) {
      setBundles([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredBundles = useMemo(() => {
    return bundles.filter((bundle) => {
     

    const nameMatch = searchTerm
      ? bundle.name?.toLowerCase().includes(searchTerm.toLowerCase())
      : false;

    const displayNameMatch = searchTerm
      ? bundle.display_name?.toLowerCase().includes(searchTerm.toLowerCase())
      : false;
      return (!searchTerm || nameMatch || displayNameMatch);
    });
  }, [bundles, searchTerm]);

  const totalPages = Math.ceil(filteredBundles.length / itemsPerPage);
  const paginatedBundles = filteredBundles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const transformBundle = (bundle) => {
    return {
      bundle_id: bundle.id,
      bundle_name: bundle.name,
      academic_year: bundle.academicYear,
      gender: bundle.gender,
      student_type: bundle.applicableStudentTypes || bundle.studentType,
      applicable_student_types: bundle.applicableStudentTypes,
      image: bundle.image,
      applicable_classes: bundle.applicableClasses,
      class_name: bundle.applicableClasses.split(", ")[0],
      bundle_total: parseFloat(bundle.totalPrice),
      isIndividualProduct: bundle.isIndividualProduct,
      isActive: bundle.isActive,
      products: bundle.bundleProducts.map((bp) => ({
        product_id: bp.product.id,
        product_name: bp.product.name,
        product_display_name: bp.product.display_name,
        unit_price: parseFloat(bp.product.unitPrice),
        quantity: bp.quantity,
        max_quantity: bp.max_quantity,
        order: bp.order,
        optional: bp.optional,
        selection_group: bp.selection_group ?? false,
        size_chart: bp.product.sizeChart,
        product_category: bp.product.category,
        product_category_order: bp.product.category_order,
      })),
    };
  };

  const onView = (bundle) => {
    setSelectedBundle(transformBundle(bundle));
  };

  const addBundleClick = () => {
    setEditBundle({
      academicYear: selectedYear,
    });
    setShowBundleForm(true);
  };

  const onEditClick = (bundle) => {
    // Transform the bundle data to match the form structure
    const bundleData = {
      id: bundle.id,
      name: bundle.name,
      display_name: bundle.display_name,
      imageUrl: bundle.image,
      academicYear: bundle.academicYear,
      gender: bundle.gender,
      studentType: bundle.studentType,
      applicableStudentTypes: bundle.applicableStudentTypes,
      applicableClasses: bundle.applicableClasses,
      isIndividualProduct: bundle.isIndividualProduct,
      isActive: bundle.isActive,
      totalPrice: parseFloat(bundle.totalPrice),
      products: bundle.bundleProducts.length > 0 ? bundle.bundleProducts.map(bp => ({
        productId: bp.product.id.toString(),
        quantity: bp.quantity,
        max_quantity: bp.max_quantity,
        order: bp.order,
        optional: bp.optional,
        selection_group: bp.selection_group
      })) : [{ productId: "", quantity: 1, optional: false, selection_group:false, order:1 }]
    };
    setEditBundle(bundleData);
    setShowBundleForm(true);
  };

  const onDeleteClick = (id) => {
    setSelectedId(id);
    setShowConfirm(true);
  };
  const onSyncClick = async (id) => {
    try {
      setLoading(true);
      const preview = await previewSyncBundle(id);
      if (preview) {
        setSyncPreviewData(preview);
        setShowSyncPreview(true);
      }
    } catch (error) {
      console.error("Error previewing bundle sync:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSync = async () => {
    if (!syncPreviewData?.bundleId) return;
    try {
      setSyncing(true);
      const res = await syncBundle(syncPreviewData.bundleId);
      if (res) {
        toast.success(
          `Sync complete: ${res.created} created, ${res.updated || 0} updated with classes, ${res.skipped} already up to date`,
          { position: "top-right" }
        );
      }
      setShowSyncPreview(false);
      setSyncPreviewData(null);
      loadBundles(selectedYear, studentType, productType);
    } catch (error) {
      console.error("Error syncing bundle:", error);
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveBundle = async (formData) => {
    try {
      setLoading(true);

      // Format bundle data for API
      const bundleData = {
        name: formData.name,
        display_name: formData.display_name,
        imageUrl: formData.imageUrl,
        academicYear: formData.academicYear,
        gender: formData.gender,
        studentType: formData.studentType,
        applicableStudentTypes: formData.applicableStudentTypes || null,
        applicableClasses: formData.applicableClasses,
        isIndividualProduct: formData.isIndividualProduct,
        isActive: formData.isActive,
        totalPrice: formData.totalPrice,
        products: formData.products.map((p) => ({
          productId: parseInt(p.productId),
          quantity: parseInt(p.quantity),
          order: parseInt(p.order) || 1,
          max_quantity: parseInt(p.max_quantity),
          optional: p.optional,
          selection_group: p.selection_group ? p.selection_group : false,
        })),
      };

      let response;
      if (editBundle && editBundle.id) {
        // Update existing bundle
        response = await updateBundle(editBundle.id, bundleData);
        if (response && (response.success || response.id)) {
          // If the academic year changed, we might want to remove it from the current view or update it
          if (response.academicYear !== selectedYear) {
            setBundles(bundles.filter((b) => b.id !== editBundle.id));
          } else {
            setBundles(
              bundles.map((bundle) =>
                bundle.id === editBundle.id ? response : bundle
              )
            );
          }
          setShowBundleForm(false);
          setEditBundle(null);
        }
      } else {
        // Create new bundle
        response = await createBundle(bundleData);
        if (response && (response.success || response.id)) {
          if (response.academicYear === selectedYear) {
            setBundles([response, ...bundles]);
          }
          setShowBundleForm(false);
          setEditBundle(null);
        }
      }
    } catch (error) {
      console.error("Error saving bundle:", error);
      toast.error("Error saving bundle", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const deleteConfirmClick = async () => {
    try {
      setLoading(true);
      await deleteBundle(selectedId);
      setBundles(bundles.filter(bundle => bundle.id !== selectedId));
      toast.success("Bundle deleted successfully", { position: "top-right" });
    } catch (error) {
      toast.error("Error deleting bundle", { position: "top-right" });
    } finally {
      setShowConfirm(false);
      setLoading(false);
      setSelectedId(null);
    }
  };

  return (
    <>
      <style>{`
        .cursor-pointer { cursor: pointer; }
        .table-responsive::-webkit-scrollbar { height: 8px; }
        .table-responsive::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 4px; }
        .filter-card { transition: all 0.3s ease; }
        .action-btn { transition: transform 0.2s; white-space: nowrap; }
        .action-btn:hover { transform: translateY(-1px); }
        .table th { white-space: nowrap; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; }
        .table td { font-size: 0.9rem; }
        .pagination-container { border-top: 1px solid #dee2e6; padding-top: 1.5rem; }
      `}</style>
      {loading && <FullPageSpinner loading={loading} />}
      <div className="bg-light min-vh-100 py-4">
        <div className="container-fluid px-lg-4">
          <div className="card shadow-sm border-0 rounded-3">
            <div className="card-header bg-white border-bottom py-3 px-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h4 className="mb-0 text-primary fw-bold d-flex align-items-center">
                  <i className="bi bi-collection-fill me-2"></i>Bundle Management
                </h4>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary rounded-pill px-3 py-2">Total: {filteredBundles.length}</span>
                </div>
              </div>
            </div>
            
            <div className="card-body p-4">
              {/* Filters & Actions Section */}
              <div className="bg-white rounded-3 p-4 mb-4 border shadow-sm filter-card">
                <div className="row g-3 align-items-end mb-4">
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                      Search Bundles
                    </label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-white border-end-0">
                        <i className="bi bi-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0 ps-0"
                        placeholder="Search by name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="col-12 col-md-4 col-lg-2">
                    <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                      Academic Year
                    </label>
                    <select 
                      className="form-select form-select-sm"
                      value={selectedYear}
                      onChange={(e) => {
                        setSelectedYear(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      {academicYears.map(year => (
                        <option key={year.year} value={year.year}>{year.year}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-6 col-md-4 col-lg-2">
                    <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                      Product Type
                    </label>
                    <select 
                      className="form-select form-select-sm"
                      value={productType}
                      onChange={(e) => setProductType(e.target.value)}
                    >
                      <option value=''>All Types</option>
                      <option value='1'>Single</option>
                      <option value='0'>Bundle</option>
                    </select>
                  </div>

                  <div className="col-6 col-md-4 col-lg-2">
                    <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                      Student Type
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={studentType}
                      onChange={(e) => setStudentType(e.target.value)}
                    >
                      <option value="">All Students</option>
                      <option value="New">New</option>
                      <option value="Existing">Existing</option>
                      <option value="Hostel">Hostel</option>
                      <option value="Hostel_New">Hostel_New</option>
                      <option value="Hostel_Existing">Hostel_Existing</option>
                    </select>
                  </div>

                  <div className="col-12 col-lg-3 text-lg-end">
                    { canAddBundle && 
                      <button
                        className="btn btn-success btn-sm action-btn px-4 fw-bold shadow-sm"
                        onClick={addBundleClick}
                      >
                        <i className="bi bi-plus-circle-fill me-1"></i> Add Bundle
                      </button>
                    }
                  </div>
                </div>
              </div>

              {/* Table Section */}
              <div className="table-responsive rounded-3 border shadow-sm bg-white">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '60px' }}>ID</th>
                      <th>Name</th>
                      <th>Academic Year</th>
                      <th>Gender</th>
                      <th>Suitable Classes</th>
                      <th>Type</th>
                      <th>Student Type</th>
                      <th className="text-end">Price</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedBundles.length > 0 ? (
                      paginatedBundles.map((bundle) => (
                        <tr key={bundle.id}>
                          <td className="text-muted small">#{bundle.id}</td>
                          <td>
                            <div className="fw-semibold text-dark">{bundle.name}</div>
                            <div className="text-muted small" style={{ fontSize: '0.75rem' }}>{bundle.display_name}</div>
                          </td>
                          <td className="small">{bundle.academicYear}</td>
                          <td>
                            <span className="badge bg-light text-dark border px-2">{bundle.gender}</span>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '150px' }} title={bundle.applicableClasses}>
                              {bundle.applicableClasses}
                            </div>
                          </td>
                          <td>
                            <span className={`badge ${bundle.isIndividualProduct ? "bg-info text-white" : "bg-warning text-dark"}`}>
                              {bundle.isIndividualProduct ? "Single":"Bundle"}
                            </span>
                          </td>
                          <td className="small">
                            <div className="text-truncate" style={{ maxWidth: '160px' }} title={bundle.applicableStudentTypes || bundle.studentType}>
                              {bundle.applicableStudentTypes || bundle.studentType}
                            </div>
                          </td>
                          <td className="text-end fw-bold">₹{bundle.totalPrice}</td>
                          <td> 
                            <span className={`badge rounded-pill ${bundle?.isActive ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                              {bundle?.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              {canViewBundleDetails && (
                                <button
                                  className="btn btn-outline-info btn-sm rounded-circle"
                                  onClick={() => onView(bundle)}
                                  title="View Bundle"
                                  style={{ width: '32px', height: '32px', padding: 0 }}
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                              )}
                              {(currentYear === bundle.academicYear) && (
                                <>
                                  {canEditBundle && (
                                    <button
                                      className="btn btn-outline-primary btn-sm rounded-circle"
                                      onClick={() => onEditClick(bundle)}
                                      title="Edit Bundle"
                                      style={{ width: '32px', height: '32px', padding: 0 }}
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                  )}
                                  {!bundle.isIndividualProduct && canSyncBundle && (
                                    <button
                                      className="btn btn-outline-warning btn-sm rounded-circle"
                                      onClick={() => onSyncClick(bundle.id)}
                                      title="Sync Bundle to Single Products"
                                      style={{ width: '32px', height: '32px', padding: 0 }}
                                    >
                                      <i className="bi bi-arrow-repeat"></i>
                                    </button>
                                  )}
                                  {canDeleteBundle && (
                                    <button
                                      className="btn btn-outline-danger btn-sm rounded-circle"
                                      onClick={() => onDeleteClick(bundle.id)}
                                      title="Delete Bundle"
                                      style={{ width: '32px', height: '32px', padding: 0 }}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-5 text-muted">
                          <i className="bi bi-collection-play-fill fs-1 d-block mb-2"></i>
                          No bundles found matching your criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Section */}
              <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3">
                <div className="d-flex align-items-center gap-3 order-2 order-md-1">
                  <div className="d-flex align-items-center bg-white border rounded px-2 py-1 shadow-sm">
                    <span className="text-muted small me-2">Show</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      className="form-select form-select-sm border-0 bg-transparent fw-bold"
                      style={{ width: 'auto', outline: 'none', boxShadow: 'none' }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-muted small ms-2">entries</span>
                  </div>
                  <div className="text-muted small border-start ps-3 d-none d-sm-block">
                    Showing <span className="fw-bold text-dark">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="fw-bold text-dark">{Math.min(currentPage * itemsPerPage, filteredBundles.length)}</span> of <span className="fw-bold text-dark">{filteredBundles.length}</span> records
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 order-1 order-md-2">
                  <button
                    className="btn btn-outline-primary btn-sm px-3 rounded-pill shadow-sm fw-bold"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  >
                    <i className="bi bi-chevron-left"></i> Prev
                  </button>
                  
                  <div className="bg-white px-3 py-1 rounded-pill border shadow-sm small fw-bold">
                    Page {currentPage} of {totalPages}
                  </div>

                  <button
                    className="btn btn-outline-primary btn-sm px-3 rounded-pill shadow-sm fw-bold"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    Next <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {selectedBundle && (
        <QuickViewModal
          bundle={selectedBundle}
          onClose={() => setSelectedBundle(null)}
          showAction={false}
        />
      )}

      {showBundleForm && (
        <BundleFormDialog
          data={editBundle}
          onSave={handleSaveBundle}
          onCancel={() => setShowBundleForm(false)}
          header={editBundle ? "Edit Bundle" : "Add Bundle"}
          selectedYear={selectedYear}
        />
      )}

      <ConfirmModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={deleteConfirmClick}
        message="Are you sure you want to delete this bundle?"
      />

      <SyncPreviewModal
        show={showSyncPreview}
        onClose={() => {
          if (!syncing) {
            setShowSyncPreview(false);
            setSyncPreviewData(null);
          }
        }}
        onConfirm={handleConfirmSync}
        previewData={syncPreviewData}
        syncing={syncing}
      />
    </>
  );
};

export default BundleManagement;
